import React, { useCallback } from 'react'
import { S as SInfoPopover } from 'features/booth/components/InfoPopover'
import Icon from 'components/Icon'
import styled from 'styled-components'
import { useDeleteItem } from 'hooks'
import { useDispatch } from 'react-redux'
import { addModal } from 'features/modals/modalSlice'
import CategoryModal from 'components/CategoryModal'
import TagModal from 'components/TagModal'
import { minBy } from 'lodash'
import { _ } from 'utils'
import { Category } from './lists/CategoryList'
import { CreateTag } from '../generated/graphql'

const S = {
    PopoverMenu: styled.div<{ direction: string }>`
        position: absolute;
        right: 0;
        z-index: 1;
        background: white;
        box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.2);
        border-radius: 2px;
        ${({ direction }) => (direction === 'up' ? `
        top: 0;
        transform: translateY(-100%);
        ` : `
        bottom: 0;
        transform: translateY(100%);
        cursor: pointer
        `)}
    `,
    PopoverItem: SInfoPopover.PopoverItem
}

interface Props {
    category: Category
    direction: string
    fetchData: () => void
    deleteCategory: (id: number) => Promise<any>
    createTag:(data: CreateTag) => Promise<void>
    updateCategory: (id, text) => Promise<any>

}

const InfoPopover = ({
    category, direction, fetchData, deleteCategory, createTag, updateCategory
}: Props) => {
    const dispatch = useDispatch()
    const { showDeleteConfirmModal } = useDeleteItem(
        deleteCategory, fetchData, {
            title: _('info_popover.category.delete.title'),
            description: _('info_popover.category.delete.description')
        }
    )
    const onDelete = useCallback(() => {
        showDeleteConfirmModal(category.id)
    }, [category, showDeleteConfirmModal])

    const showCategoryModal = useCallback(() => {
        dispatch(addModal({
            Component: ({ close }: { close: () => void }) => (
                <CategoryModal
                    updateCategory={updateCategory}
                    createCategory={async () => {}}
                    close={close}
                    onSubmitted={fetchData}
                    category={category}
                />
            ),
            size: 'xl',
            shouldNotWrap: true
        }))
    }, [dispatch, updateCategory, fetchData, category])

    const showTagModal = useCallback(() => {
        const tag = minBy(category.tags, 'order')
        dispatch(addModal({
            Component: ({ close }: { close: () => void }) => (
                <TagModal
                    close={close}
                    onSubmitted={fetchData}
                    categoryId={category.id.toString()}
                    minOrder={tag ? tag.order : 0}
                    createTag={createTag}
                />
            ),
            size: 'xl',
            shouldNotWrap: true
        }))
    }, [dispatch, fetchData, category, createTag])

    return (
        <S.PopoverMenu direction={direction}>
            <S.PopoverItem onClick={showTagModal}>
                <Icon icon="plus" size={18} />
                {_('info_popover.category.add_tag')}
            </S.PopoverItem>
            <S.PopoverItem onClick={showCategoryModal}>
                <Icon icon="edit" size={18} />
                {_('info_popover.category.edit')}
            </S.PopoverItem>
            <S.PopoverItem onClick={onDelete}>
                <Icon icon="trash" size={18} />
                {_('info_popover.category.delete')}
            </S.PopoverItem>
        </S.PopoverMenu>
    )
}

export default InfoPopover
